/* eslint-disable camelcase */
import Price from '@/classes/Price'
import { isArray, isNumber, isObject, isString, isBoolean, isNullish } from 'typechecker'
import PaymentStatus from '@/enums/PaymentStatus'
import IdNameObject from '@/interfaces/IdNameObject'
import Vendor from '@/enums/Vendor'
import { ReservationResponse, InvoiceScope, MileagePolicyType, Erp, OptionalItemType, ApiResponseRewardType, DiscountMethod, ApiResponseShopCancellationPolicy, GetReservationCancelResponse } from '@/types/ggo-api/responses/v2/Reservation'
import dayjs from 'dayjs'
import useTranslation from '@/utils/v2/useTranslation'
import Order, { OrderStatus, OrderUtils } from '@/classes/Order'
import { GetCancelOrderResponse, GetCommercialInvoiceResponse } from '@/types/ggo-api/responses/Order'

type OptionalItemArg = IdNameObject & {
  price: number
  quantity: number
  refundable: number
}
type OptionalItem = IdNameObject & {
  price: Price
  quantity: number
  refundable: boolean
}

export type OrderDetailArgument = {
  status?: OrderStatus
  order_status?: OrderStatus
  vendor?: Vendor;
  member_id?: number;
  trackingCode?: string;
  referenceCode?: any;
  store_city?: string;
  name?: string;
  identity?: string;
  passport?: string;
  birthday?: string;
  mobile?: string;
  email?: string;
  car_license?: string;
  invoiceInfo?: string;
  car_id?: number;
  startStoreId?: number | string;
  startStoreName?: string;
  endStoreId?: number | string;
  endStoreName?: string;
  cancelNote?: {
    show: boolean,
    text: string
  };
  optional_items?: OptionalItemArg[];
  paymentMethods?: IdNameObject[];
  payment_precaution?: string[];
  created_at: string; // orderTime
  pay_time? : string;
  paid?: PaymentStatus;
  coupon?: number;
  promocode?: string;
  payment_price?: number; // paymentPrice
  credit_last_digits?: any; // string to number
  invoice_no? : string;
  discount?: number;
  car_price?: number // carPrice
  countryCode?: string
  pay_price?: number; // payPrice
  cancelledAt?: string; // 需要轉換 : cancelledAt -> cancel_at
  cancel_at?: string;
  refund?: Price; // 需要轉換 to Price
  refundPercentage?: number;
  start_flight?: {
    date: string,
    number: string
  }; // startFlight
  end_flight?: {
    date: string,
    number: string
  }; // endFlight
  start_pick_up?: boolean; // startPickUp
  end_pick_up?: boolean; // endPickUp
  referralAds? : number[];
  isInvoiceUpdatable?: boolean;
  orderNum?: string;
  erpOrderNum?: string;
  startTime?: string;
  endTime?: string;
  productName?: string;
  startStorePhone?: string;
  endStorePhone?: string;
  startStoreAddress?: string;
  endStoreAddress?: string;
  storeServices?: any[];
  car_brand?: string;
  car_model?: string;
  car_age?: number;
  seats?: number;
  doors?: number;
  exhaust?: string;
  car_type?: string;
  car_image?: string;
  car_equipments?: IdNameObject[];
  car_insurances?: IdNameObject[];
  transmission?: string;

  store_policies?: IdNameObject[];
  distance_rule?: string;
  overDistanceRule?: string;
  fuelRule?: string;
  cancelRule?: number;
  cancelRuleText?: string;
  cancel_rule?: string;

  price_currency_code?: string;
  price_currency_sign?: string;

  useGoPoints?: number;
  feedbackGoPoints: number;
  hasCancellationProtection?: boolean;
  isReservation?: boolean;
}

export default class OrderDetail extends Order {
  key?: Symbol = Symbol('')
  /** 付款狀態 */
  paymentStatus: PaymentStatus
  /** 訂單會員編號 */
  member_id?: number
  /** 下單處 */
  trackingCode?: string
  /** referenceCode */
  referenceCode?: any
  /** 店家所在地 */
  store_city?: string
  /** 訂單人名稱 */
  name?: string
  /** 訂單人身分證 */
  identity?: string
  /** 訂單人護照 */
  passport?: string
  /** 訂單人生日 */
  birthday?: string
  /** 訂單人手機 */
  mobile?: string
  /** 訂單人email */
  email?: string
  /** 車牌 */
  car_license?: string
  /** 收據資訊 */
  invoiceInfo?: string

  // 去車API撈
  carBrand?: string
  /** 車輛型號 */
  carModel?: string
  /** 車齡 */
  carAge?: number
  /** 座位數量 */
  seats?: number
  /** 車門 */
  doors?: number
  /** 排氣量 */
  exhaust?: string
  /** 車型 */
  carType?: string
  /** 車輛照片 */
  carImage?: string
  /** 車輛設備 */
  carEquipments: IdNameObject[] = []
  /** 車輛保險 */
  carInsurances: IdNameObject[] = []

  transmission?: string

  /** 取消備註 */
  cancelNote?: {
    show: boolean,
    text: string
  }

  optionalItems: OptionalItem[] = []

  /** 商店政策 */
  storePolicies: IdNameObject[] = []
  distanceRule?: string
  overDistanceRule?: string
  fuelRule?: string
  cancelRule?: number
  cancelRuleText?: string

  /** 付款方式 */
  paymentMethods: IdNameObject[] = []

  /** 注意事項 */
  paymentPrecaution: string[] = []

  /** 優惠券 */
  coupon?: number

  /** 促銷碼 */
  promocode?: string

  /** 付款價格 */
  paymentPrice?: Price

  /** 付款價格 */
  payPrice?: Price

  /** 折扣 */
  discount?: number

  /** 折扣 */
  discountPrice?: Price

  /** 車價 */
  carPrice?: Price

  /** 租令地國家 */
  countryCode?: string

  /** 去程航班 */
  startFlight?: {
    date: string,
    number: string
  }

  /** 返程航班 */
  endFlight?: {
    date: string,
    number: string
  }

  /** 去程機場接送 */
  startPickUp?: boolean

  /** 返程機場接送 */
  endPickUp?: boolean

  /** 收據可以變更 */
  isInvoiceUpdatable?: boolean

  startStorePhone?: string
  endStorePhone?: string
  startStoreAddress?: string
  endStoreAddress?: string
  storeServices: any[] = []

  refund?: Price

  /** 原始價格(折扣前) */
  totalPrice?: Price
  /** 使用的 go points 數量 */
  useGoPoints?: number;
  /** 回饋的 go points 數量 */
  feedbackGoPoints?: number;

  /** 是否有退訂保障 */
  hasCancellationProtection?: boolean

  /** 2.0 訂單 */
  isReservation: boolean = false

  constructor (orderDetail: OrderDetailArgument) {
    super(orderDetail)

    this.key = Symbol('')

    if (isNumber(orderDetail.paid)) this.paymentStatus = orderDetail.paid ?? PaymentStatus.PENDING
    else this.paymentStatus = PaymentStatus.PENDING

    if (orderDetail.member_id && isNumber(orderDetail.member_id)) this.member_id = orderDetail.member_id
    if (orderDetail.trackingCode && isString(orderDetail.trackingCode)) this.trackingCode = orderDetail.trackingCode
    if (orderDetail.referenceCode) this.referenceCode = orderDetail.referenceCode
    if (orderDetail.store_city && isString(orderDetail.store_city)) this.store_city = orderDetail.store_city
    if (orderDetail.name && isString(orderDetail.name)) this.name = orderDetail.name
    if (orderDetail.identity && isString(orderDetail.identity)) this.identity = orderDetail.identity
    if (orderDetail.passport && isString(orderDetail.passport)) this.passport = orderDetail.passport
    if (orderDetail.birthday && isString(orderDetail.birthday)) this.birthday = orderDetail.birthday
    if (orderDetail.mobile && isString(orderDetail.mobile)) this.mobile = orderDetail.mobile
    if (orderDetail.email && isString(orderDetail.email)) this.email = orderDetail.email
    if (orderDetail.car_license && isString(orderDetail.car_license)) this.car_license = orderDetail.car_license
    if (orderDetail.invoiceInfo && isString(orderDetail.invoiceInfo)) this.invoiceInfo = orderDetail.invoiceInfo
    if (orderDetail.cancelNote && isObject(orderDetail.cancelNote)) this.cancelNote = orderDetail.cancelNote
    if (orderDetail.optional_items && isArray(orderDetail.optional_items)) {
      this.optionalItems = orderDetail.optional_items.map((item: OptionalItemArg) => {
        return {
          id: item.id,
          name: item.name,
          price: new Price(item.price, {
            code: orderDetail.price_currency_code ?? 'NTD',
            sign: orderDetail.price_currency_sign ?? '$',
          }),
          quantity: item.quantity,
          refundable: Boolean(item.refundable),
        }
      })
    }
    if (orderDetail.paymentMethods && isArray(orderDetail.paymentMethods)) this.paymentMethods = orderDetail.paymentMethods
    if (orderDetail.payment_precaution && isArray(orderDetail.payment_precaution)) this.paymentPrecaution = orderDetail.payment_precaution
    if (orderDetail.coupon && isNumber(orderDetail.coupon)) this.coupon = orderDetail.coupon
    if (orderDetail.promocode && isString(orderDetail.promocode)) this.promocode = orderDetail.promocode
    if (orderDetail.payment_price && isNumber(orderDetail.payment_price)) {
      this.paymentPrice = new Price(orderDetail.payment_price, {
        code: orderDetail.price_currency_code ?? 'NTD',
        sign: orderDetail.price_currency_sign ?? '$',
      })
    }
    if (orderDetail.pay_price && isNumber(orderDetail.pay_price)) {
      this.payPrice = new Price(orderDetail.pay_price, {
        code: orderDetail.price_currency_code ?? 'NTD',
        sign: orderDetail.price_currency_sign ?? '$',
      })
    }
    if (orderDetail.discount !== undefined && orderDetail.discount !== null && isNumber(orderDetail.discount)) {
      this.discount = orderDetail.discount
      this.discountPrice = new Price(orderDetail.discount, {
        code: orderDetail.price_currency_code ?? 'NTD',
        sign: orderDetail.price_currency_sign ?? '$',
      })
    }
    if (orderDetail.car_price && isNumber(orderDetail.car_price)) {
      this.carPrice = new Price(orderDetail.car_price, {
        code: orderDetail.price_currency_code ?? 'NTD',
        sign: orderDetail.price_currency_sign ?? '$',
      })
    }
    if (orderDetail.countryCode && isString(orderDetail.countryCode)) this.countryCode = orderDetail.countryCode
    if (orderDetail.start_flight && isObject(orderDetail.start_flight)) this.startFlight = orderDetail.start_flight
    if (orderDetail.end_flight && isObject(orderDetail.end_flight)) this.endFlight = orderDetail.end_flight
    if (orderDetail.start_pick_up && isBoolean(orderDetail.start_pick_up)) this.startPickUp = orderDetail.start_pick_up
    if (orderDetail.end_pick_up && isBoolean(orderDetail.end_pick_up)) this.endPickUp = orderDetail.end_pick_up
    if (orderDetail.isInvoiceUpdatable && isBoolean(orderDetail.isInvoiceUpdatable)) this.isInvoiceUpdatable = orderDetail.isInvoiceUpdatable
    if (orderDetail.startStorePhone && isString(orderDetail.startStorePhone)) this.startStorePhone = orderDetail.startStorePhone
    if (orderDetail.endStorePhone && isString(orderDetail.endStorePhone)) this.endStorePhone = orderDetail.endStorePhone
    if (orderDetail.startStoreAddress && isString(orderDetail.startStoreAddress)) this.startStoreAddress = orderDetail.startStoreAddress
    if (orderDetail.endStoreAddress && isString(orderDetail.endStoreAddress)) this.endStoreAddress = orderDetail.endStoreAddress
    if (orderDetail.storeServices && isArray(orderDetail.storeServices)) this.storeServices = orderDetail.storeServices
    if (orderDetail.car_brand && isString(orderDetail.car_brand)) this.carBrand = orderDetail.car_brand
    if (orderDetail.car_model && isString(orderDetail.car_model)) this.carModel = orderDetail.car_model
    if (orderDetail.car_age && isNumber(orderDetail.car_age)) this.carAge = orderDetail.car_age
    if (orderDetail.seats && isNumber(orderDetail.seats)) this.seats = orderDetail.seats
    if (orderDetail.doors && isNumber(orderDetail.doors)) this.doors = orderDetail.doors
    if (orderDetail.exhaust && isString(orderDetail.exhaust)) this.exhaust = orderDetail.exhaust
    if (orderDetail.car_type && isString(orderDetail.car_type)) this.carType = orderDetail.car_type
    if (orderDetail.car_image && isString(orderDetail.car_image)) this.carImage = orderDetail.car_image
    if (orderDetail.car_equipments && isArray(orderDetail.car_equipments)) this.carEquipments = orderDetail.car_equipments
    if (orderDetail.car_insurances && isArray(orderDetail.car_insurances)) this.carInsurances = orderDetail.car_insurances
    if (orderDetail.transmission && isString(orderDetail.transmission)) this.transmission = orderDetail.transmission

    if (orderDetail.store_policies && isArray(orderDetail.store_policies)) this.storePolicies = orderDetail.store_policies
    if (orderDetail.distance_rule && isString(orderDetail.distance_rule)) this.distanceRule = orderDetail.distance_rule
    if (orderDetail.overDistanceRule && isString(orderDetail.overDistanceRule)) this.overDistanceRule = orderDetail.overDistanceRule
    if (orderDetail.fuelRule && isString(orderDetail.fuelRule)) this.fuelRule = orderDetail.fuelRule
    if (orderDetail.cancelRule && isNumber(orderDetail.cancelRule)) this.cancelRule = orderDetail.cancelRule
    if (orderDetail.cancel_rule && isString(orderDetail.cancel_rule)) this.cancelRuleText = orderDetail.cancel_rule
    if (orderDetail.cancelRuleText && isString(orderDetail.cancelRuleText)) this.cancelRuleText = orderDetail.cancelRuleText

    if (orderDetail.referralAds && isArray(orderDetail.referralAds)) this.referralAds = orderDetail.referralAds

    /** 把實際付款和折扣金額加總回去 */
    if (isNumber(orderDetail.pay_price) && isNumber(orderDetail.discount)) {
      this.totalPrice = new Price(orderDetail.pay_price! + orderDetail.discount!, {
        code: orderDetail.price_currency_code ?? 'NTD',
        sign: orderDetail.price_currency_sign ?? '$',
      })
    }
    if (isNumber(orderDetail.useGoPoints)) this.useGoPoints = orderDetail.useGoPoints
    if (isNumber(orderDetail.feedbackGoPoints)) this.feedbackGoPoints = orderDetail.feedbackGoPoints
    // 若 optional items 裡有 id 為 2 (退訂保障)，則有退訂保障
    this.hasCancellationProtection = this.optionalItems.some(item => item.id === 2)
    if (isBoolean(orderDetail.isReservation)) this.isReservation = orderDetail.isReservation ?? false
  }
}

/**
 * 2.0 訂單 adapter
 */
export class OrderDetailUtils {/**
  * 解析商家退訂政策
  * @param policy 後端返回的退訂政策
  * @param lang 語言
  * @returns 退訂政策文字
  */
  static parseShopCancellationPolicyText (
    policy: ApiResponseShopCancellationPolicy,
    lang: string = 'en',
  ) {
    const { t } = useTranslation()
    // 找出免費退訂政策
    const freeCancellationPolicy = policy.rules.find(
      item => item.refundPercentage === 100,
    )
    // 找出其他退訂政策
    const otherCancellationPolicy = policy.rules
      .filter(item => item.refundPercentage !== 100)
      .sort((a, b) => b.daysBeforeCancellation - a.daysBeforeCancellation)
    let freeCancellationPolicyText = ''
    let otherCancellationPolicyText: string[] = []

    if (freeCancellationPolicy) {
      freeCancellationPolicyText = t(
        lang,
        'cancellationPolicy.freeCancellation.value',
        [String(freeCancellationPolicy.daysBeforeCancellation)],
      )
    }
    if (otherCancellationPolicy.length > 0) {
      otherCancellationPolicyText = otherCancellationPolicy.map(item =>
        t(lang, 'cancellationPolicy.percentageCancellation.value', [
          String(item.daysBeforeCancellation),
          String(item.refundPercentage),
        ]),
      )
    }
    // 找出其他退訂政策中，最後一個可退訂的日期
    const lastRefundableDay =
     otherCancellationPolicy[otherCancellationPolicy.length - 1]
       ?.daysBeforeCancellation
    return t(lang, 'cancellationPolicy.skeleton.value', [
      [freeCancellationPolicyText, otherCancellationPolicyText.join('')].join(
        ' ',
      ),
      lastRefundableDay ? String(lastRefundableDay) : '',
    ])
  }

  static parseOrderDetail (reservation: ReservationResponse, lang: string): OrderDetail {
    const { t } = useTranslation()

    const { carPrice, optionalItemsPrice, discountPrice } = OrderUtils.parseApiResponseReservationPrices(reservation)

    const shopServiceMap =
      reservation.shopInfo.metadata?.store_services?.reduce((map, service) => {
        service.items.forEach((item) => {
          map.set(item.id, {
            name: service.name,
            item: item.name,
          })
        })
        return map
      }, new Map<string, {
        name: string,
        item: string,
      }>())

    const orderDetail = new OrderDetail({
      orderNum: reservation.serialNumber,
      erpOrderNum: reservation.erpSerialNumber,
      // TODO: 2.0 shop id 與 1.0 erp id 不互通，需要用 sku 做 mapping
      startStoreId: reservation.shopInfo.sku ? OrderUtils.parseStoreId(reservation.shopInfo.erpId, reservation.shopInfo.sku, reservation.shopInfo.id) : reservation.shopInfo.id,
      startStoreName: reservation.shopInfo.name,
      startStorePhone: reservation.shopInfo.telephone,
      startStoreAddress: reservation.shopInfo.address,
      // TODO: 2.0 shop id 與 1.0 erp id 不互通，需要用 sku 做 mapping
      endStoreId: reservation.shopInfo.sku ? OrderUtils.parseStoreId(reservation.shopInfo.erpId, reservation.shopInfo.sku, reservation.shopInfo.id) : reservation.shopInfo.id, // Assuming same store for now
      endStoreName: reservation.shopInfo.name, // Assuming same store for now
      endStorePhone: reservation.shopInfo.telephone, // Assuming same store for now
      endStoreAddress: reservation.shopInfo.address, // Assuming same store for now
      created_at: dayjs(reservation.createdAt).format('YYYY-MM-DD'),
      startTime: dayjs(reservation.localPickUp).format('YYYY-MM-DD HH:mm'),
      endTime: dayjs(reservation.localDropOff).format('YYYY-MM-DD HH:mm'),
      name: `${reservation.userInfo.firstName} ${reservation.userInfo.lastName}`,
      identity: reservation.userInfo.travelDocumentNumber,
      passport: reservation.userInfo.travelDocumentNumber,
      birthday: reservation.userInfo.birthday,
      mobile: reservation.userInfo.phoneNumber,
      email: reservation.userInfo.email,
      car_license: '',
      invoiceInfo: this.parseInvoiceInfo(reservation, lang),
      car_brand: reservation.carInfo.metadata?.make,
      car_model: reservation.carInfo.name,
      car_age: Number(reservation.carInfo.metadata?.age),
      seats: Number(reservation.carInfo.seat),
      exhaust: reservation.carInfo.metadata?.fuel,
      car_image: reservation.carInfo.images[0],
      car_equipments: reservation.carInfo.equipments?.map(eq => ({
        id: Number(eq.value),
        name: t(lang, `car.equipment.${eq.value}`),
      })) ?? [],
      car_insurances: reservation.carInfo.includedInsurances?.map((insurance, index) => ({
        id: index,
        name: insurance,
      })) ?? [],
      transmission: reservation.carInfo.transmission.value,
      store_policies: reservation.shopInfo.policies?.map(policy => ({
        id: Number(policy.key),
        name: policy.value,
      })) ?? [],
      distance_rule: reservation.carInfo.metadata?.mileage_policy
        ? t(lang, `car.mileagePolicy.${reservation.carInfo.metadata.mileage_policy.type === MileagePolicyType.Limited ? 'limited' : 'unlimited'}.text`, [
        reservation.carInfo.metadata?.mileage_policy.max_km_per_day ?? '0',
        reservation.carInfo.metadata?.mileage_policy.extra_km_charge?.currencyCode ?? 'TWD',
        reservation.carInfo.metadata?.mileage_policy.extra_km_charge?.amount ?? '0',
          ])
        : undefined,
      fuelRule: reservation.carInfo.metadata?.fuel_policy.type ? t(lang, `car.fuelPolicy.${reservation.carInfo.metadata.fuel_policy.type}.text`) : undefined,
      cancelRuleText: this.parseShopCancellationPolicyText(reservation.shopInfo.cancellationPolicy, lang),
      payment_precaution: [
        t(lang, 'paymentPrecaution.1.text'),
        t(lang, 'paymentPrecaution.2.text'),
        t(lang, 'paymentPrecaution.3.text'),
        t(lang, 'paymentPrecaution.4.text'),
        t(lang, 'paymentPrecaution.5.text'),
      ],
      pay_time: reservation.paidAt ? dayjs(reservation.paidAt).format('YYYY-MM-DD HH:mm:ss') : undefined,
      // hertz 的 paid 始終為 0
      paid: reservation.shopInfo.erpId === Erp.Hertz ? 0 : (reservation.paidAt ? 1 : 0),
      payment_price: carPrice.value + optionalItemsPrice.value - discountPrice.value,
      pay_price: carPrice.value + optionalItemsPrice.value - discountPrice.value,
      price_currency_code: reservation.carInfo.saleMoney.currencyCode,
      price_currency_sign: '$',
      cancel_at: reservation.cancelledAt ? dayjs(reservation.cancelledAt).format('YYYY-MM-DD HH:mm:ss') : undefined,
      referralAds: reservation.referralInfo,
      optional_items: reservation.optionalInfo?.items.map(item => ({
        id: item.id,
        name: t(lang, `optionalItem.${item.id}.title`),
        price: item.optionalMoney.amount,
        quantity: item.quantity,
        refundable: item.type === OptionalItemType.Refundable ? 1 : 0,
      })) ?? [],
      useGoPoints: reservation.discountInfo.type === DiscountMethod.GoPoint
        ? reservation.discountInfo.goPoints.amount
        : 0,
      feedbackGoPoints: reservation.rewardInfo.type === ApiResponseRewardType.GoPoints
        ? reservation.rewardInfo.goPoints.amount
        : 0,
      refund: new Price(OrderUtils.parseApiResponseReservationRefundPrice(reservation).price.value, {
        code: OrderUtils.parseApiResponseReservationRefundPrice(reservation).price.currency.code,
        sign: OrderUtils.parseApiResponseReservationRefundPrice(reservation).price.currency.sign,
      }),
      refundPercentage: OrderUtils.parseApiResponseReservationRefundPrice(reservation).percentage,
      discount: discountPrice.value,
      car_price: carPrice.value,
      paymentMethods: [
        {
          id: 1,
          name: t(lang, `paymentMethod.${reservation.paymentInfo?.method ?? 'CREDIT_CARD'}`),
        },
      ],
      storeServices:
      reservation.metadata?.erp_id === Erp.Gogoout_20 ||
      reservation.metadata?.erp_id === Erp.Gogoout_18 ||
      reservation.metadata?.erp_id === Erp.Gogoout_16
        ? (reservation.metadata?.data.service_items
            ?.map(item => shopServiceMap?.get(item))
            .filter(item => !isNullish(item)) ?? [])
        : [],
      isReservation: true,
    })

    return orderDetail
  }

  static parseCancelResponse (orderDetail: OrderDetail, cancelResponse: GetReservationCancelResponse): GetCancelOrderResponse {
    // 若 rules 裡有 1 天前可 100% 退訂，則有退訂保障
    const hasCancellationProtection = cancelResponse.cancelInfo.rules.some(rule => rule.daysBeforeCancel === 1 && rule.refundPercent === 100)
    return {
      hasCancellationProtection,
      order_no: orderDetail.orderNum ?? '',
      price: {
        value: orderDetail.totalPrice?.value ?? 0,
        currency: {
          code: orderDetail.totalPrice?.currency.code ?? 'TWD',
          sign: '$',
        },
      },
      refund: {
        value: cancelResponse.cancelInfo.selectedRule?.refundMoney.amount ?? 0,
        currency: {
          code: cancelResponse.cancelInfo.selectedRule?.refundMoney.currencyCode ?? 'TWD',
          sign: '$',
        },
      },
      refundPercentage: cancelResponse.cancelInfo.selectedRule?.refundPercent ?? 0,
      cancelText: orderDetail.cancelRuleText,
      isReservation: true,
    }
  }

  static parseCommercialInvoiceResponse (reservation: ReservationResponse): GetCommercialInvoiceResponse {
    const isCompanyInvoice = reservation.invoiceInfo.type === InvoiceScope.Company
    const { carPrice, optionalItemsPrice, discountPrice } = OrderUtils.parseApiResponseReservationPrices(reservation)

    return {
      buyer_name: isCompanyInvoice ? (reservation.invoiceInfo.company?.name ?? '') : `${reservation.userInfo.firstName} ${reservation.userInfo.lastName}`,
      buyer_mobile: reservation.userInfo.phoneNumber,
      buyer_email: reservation.userInfo.email,
      start_time: dayjs(reservation.localPickUp).format('YYYY-MM-DD HH:mm'),
      end_time: dayjs(reservation.localDropOff).format('YYYY-MM-DD HH:mm'),
      store_name: reservation.shopInfo.name,
      store_address: reservation.shopInfo.address,
      total_amount: {
        value: carPrice.value + optionalItemsPrice.value - discountPrice.value,
        currency: {
          code: reservation.carInfo.saleMoney.currencyCode,
          sign: '$',
        },
      },
      invoice_time: dayjs(reservation.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      company_name: isCompanyInvoice ? (reservation.invoiceInfo.company?.name ?? '') : null,
      company_code: isCompanyInvoice ? (reservation.invoiceInfo.company?.ubn ?? '') : null,
    }
  }

  static parseInvoiceInfo (
    reservation: ReservationResponse,
    lang: string = 'en',
  ): string {
    const { t } = useTranslation()
    const invoiceInfo = reservation.invoiceInfo
    const scopeString =
      invoiceInfo.type === InvoiceScope.Personal
        ? t(lang, 'reservation.detail.invoice.personal.title')
        : t(lang, 'reservation.detail.invoice.company.title')

    if (invoiceInfo.type === InvoiceScope.Company) {
      return `${scopeString} - (${invoiceInfo.company?.name ?? ''}/${invoiceInfo.company?.ubn ?? ''})`
    }
    if (invoiceInfo.type === InvoiceScope.Personal) {
      return scopeString
    }
    return ''
  }
}
